import { CHARACTERS, MUSIC, QUOTES, LOCATIONS, TRIVIA, CRAFTS, SILHOUETTES } from "../data";
import {
  CHARACTERS_MAP,
  MUSIC_MAP,
  QUOTES_MAP,
  LOCATIONS_MAP,
  TRIVIA_MAP,
  CRAFTS_MAP,
  SILHOUETTES_MAP,
} from "../maps";
import { getDailyItem } from "./gameLogic";

/** @type {Object<string, {data: Array, map: Array<number>, max: number, statsKey: string}>} */
const MODE_CONFIGS = {
  chars: { data: CHARACTERS, map: CHARACTERS_MAP, max: 10, statsKey: "character" },
  music: { data: MUSIC, map: MUSIC_MAP, max: 6, statsKey: "music" },
  quotes: { data: QUOTES, map: QUOTES_MAP, max: 6, statsKey: "quote" },
  locations: { data: LOCATIONS, map: LOCATIONS_MAP, max: 5, statsKey: "location" },
  trivia: { data: TRIVIA, map: TRIVIA_MAP, max: 2, statsKey: "trivia" },
  crafts: { data: CRAFTS, map: CRAFTS_MAP, max: 6, statsKey: "crafts" },
  silhouette: { data: SILHOUETTES, map: SILHOUETTES_MAP, max: 5, statsKey: "silhouette" },
};

/**
 * Scans all localStorage entries prefixed with "kisekidle-" and rebuilds
 * per-mode stats, overall stats, streak data, perfect-day tracking, and
 * collection unlocks from scratch. Useful for data recovery or migration.
 *
 * Side effects:
 *  - Writes "kisekidle-stats", "kisekidle-days-active", "kisekidle-perfect-days".
 *  - Writes "kisekidle-collection*" keys for all 7 modes.
 *
 * @returns {Object} The fully rebuilt stats object.
 */
export const recalculateAllStats = () => {
  
  const stats = {
    overall: { games: 0, wins: 0, winRate: "0%", daysActive: 0, streak: 0, perfectGames: 0 },
    character: { played: 0, won: 0, winRate: "0%", avgGuesses: 0, currentStreak: 0, bestStreak: 0, firstTryWins: 0, closeCalls: 0 },
    quote: { played: 0, won: 0, winRate: "0%", avgGuesses: 0, currentStreak: 0, bestStreak: 0, firstTryWins: 0, closeCalls: 0 },
    music: { played: 0, won: 0, winRate: "0%", avgGuesses: 0, currentStreak: 0, bestStreak: 0, firstTryWins: 0, closeCalls: 0 },
    location: { played: 0, won: 0, winRate: "0%", avgGuesses: 0, currentStreak: 0, bestStreak: 0, firstTryWins: 0, closeCalls: 0 },
    trivia: { played: 0, won: 0, winRate: "0%", avgGuesses: 0, currentStreak: 0, bestStreak: 0, firstTryWins: 0, closeCalls: 0 },
    crafts: { played: 0, won: 0, winRate: "0%", avgGuesses: 0, currentStreak: 0, bestStreak: 0, firstTryWins: 0, closeCalls: 0 },
    silhouette: { played: 0, won: 0, winRate: "0%", avgGuesses: 0, currentStreak: 0, bestStreak: 0, firstTryWins: 0, closeCalls: 0 },
  };

  const collections = {
    chars: new Set(),
    music: new Set(),
    quotes: new Set(),
    locations: new Set(),
    trivia: new Set(),
    crafts: new Set(),
    silhouette: new Set(),
  };

  const daysPlayed = new Set();
  const perfectDays = new Set();
  const historyByDate = {}; // { "2026-05-01": { chars: true, music: false, ... } }

  // 1. Scan LocalStorage for game records
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith("kisekidle-")) continue;

    const parts = key.split("-");
    if (parts.length < 3) continue;

    const modeKey = parts[1];
    const dateStr = parts.slice(2).join("-");
    const config = MODE_CONFIGS[modeKey];
    if (!config) continue;

    try {
      const storedItem = localStorage.getItem(key);
      if (!storedItem) continue;

      const record = JSON.parse(storedItem);
      const guesses = record.guesses || [];
      if (guesses.length === 0) continue;

      daysPlayed.add(dateStr);
      if (!historyByDate[dateStr]) historyByDate[dateStr] = {};

      // Determine Win
      let isWin = record.hasWon === true;
      const dateParts = dateStr.split("-").map(Number);
      const dateObj = new Date(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2], 12, 0, 0));
      const target = getDailyItem(config.data, dateObj, config.map);

      if (!isWin && target) {
        const lastGuess = guesses[guesses.length - 1];
        if (typeof lastGuess === "object" && lastGuess !== null) {
          isWin = String(lastGuess.id) === String(target.id) || 
                  (lastGuess.name !== undefined && target.name !== undefined && String(lastGuess.name) === String(target.name)) ||
                  (lastGuess.character !== undefined && target.character !== undefined && String(lastGuess.character) === String(target.character));
        } else if (typeof lastGuess === "string") {
          isWin = lastGuess.toLowerCase() === (target.name || target.character || target.title || target.locationName || target.answer || "").toLowerCase();
        }
      }

      historyByDate[dateStr][modeKey] = isWin;

      // Update per-mode base stats
      const ms = stats[config.statsKey];
      ms.played += 1;
      if (isWin) {
        const totalPrev = parseFloat(ms.avgGuesses) * ms.won || 0;
        ms.won += 1;
        ms.avgGuesses = ((totalPrev + guesses.length) / ms.won).toFixed(1);
        if (guesses.length === 1) ms.firstTryWins += 1;
        if (guesses.length === config.max) ms.closeCalls += 1;

        // --- REBUILD COLLECTIONS ---
        if (target) {
          if (modeKey === 'silhouette') {
            // Silhouettes use target.id or character name
            collections.silhouette.add(String(target.id || target.character));
          } else if (target.id) {
            collections[modeKey].add(String(target.id));
          }
        }
      }

      stats.overall.games += 1;
      if (isWin) stats.overall.wins += 1;

    } catch (e) {
      console.error(`Error parsing ${key}:`, e);
    }
  }

  // 2. Calculate Streaks and Perfect Games
  const sortedDates = Array.from(daysPlayed).sort();

  // Per-mode streaks
  Object.keys(MODE_CONFIGS).forEach(modeKey => {
    const config = MODE_CONFIGS[modeKey];
    const ms = stats[config.statsKey];
    let currentStreak = 0;
    let bestStreak = 0;

    sortedDates.forEach(date => {
      const results = historyByDate[date];
      if (results[modeKey] === undefined) return;

      if (results[modeKey] === true) {
        currentStreak += 1;
        if (currentStreak > bestStreak) bestStreak = currentStreak;
      } else {
        currentStreak = 0;
      }
    });
    ms.currentStreak = currentStreak;
    ms.bestStreak = bestStreak;
    if (ms.played > 0) {
      ms.winRate = Math.round((ms.won / ms.played) * 100) + "%";
    }
  });

  // Overall Streaks and Perfect Games
  let overallStreak = 0;
  sortedDates.forEach(date => {
    const results = historyByDate[date];
    const modeEntries = Object.keys(results);
    const winCount = modeEntries.filter(m => results[m] === true).length;

    if (winCount === 7) {
      perfectDays.add(date);
    }

    if (winCount > 0) {
      overallStreak += 1;
    } else {
      overallStreak = 0;
    }
  });

  stats.overall.daysActive = daysPlayed.size;
  stats.overall.perfectGames = perfectDays.size;
  stats.overall.streak = overallStreak;
  if (stats.overall.games > 0) {
    stats.overall.winRate = Math.round((stats.overall.wins / stats.overall.games) * 100) + "%";
  }

  // Save to LocalStorage
  localStorage.setItem("kisekidle-stats", JSON.stringify(stats));
  localStorage.setItem("kisekidle-days-active", JSON.stringify(Array.from(daysPlayed)));
  localStorage.setItem("kisekidle-perfect-days", JSON.stringify(Array.from(perfectDays)));

  // Save collections
  localStorage.setItem("kisekidle-collection", JSON.stringify(Array.from(collections.chars)));
  localStorage.setItem("kisekidle-collection-music", JSON.stringify(Array.from(collections.music)));
  localStorage.setItem("kisekidle-collection-quotes", JSON.stringify(Array.from(collections.quotes)));
  localStorage.setItem("kisekidle-collection-locations", JSON.stringify(Array.from(collections.locations)));
  localStorage.setItem("kisekidle-collection-trivia", JSON.stringify(Array.from(collections.trivia)));
  localStorage.setItem("kisekidle-collection-crafts", JSON.stringify(Array.from(collections.crafts)));
  localStorage.setItem("kisekidle-collection-silhouette", JSON.stringify(Array.from(collections.silhouette)));

  return stats;
};
